import { IconMagnifyingGlass } from 'central-icons/IconMagnifyingGlass';
import { useMemo, useRef } from 'react';
import Select, {
  type ActionMeta,
  components,
  type DropdownIndicatorProps,
  type GroupBase,
  type OnChangeValue,
  type OptionProps,
  type Props,
  type PropsValue,
} from 'react-select';

import { CustomOption } from './option';
import { sortSelectedOptionsFirst } from './utils';

export type Option<T = unknown> = {
  entity?: T;
  value: string;
  label: string;
  isDisabled?: boolean;
  disabled?: boolean;
  limit?: number;
  info?: string;
};

type SelectionListSelectProps<IsMulti extends boolean> = Props<
  Option,
  IsMulti
> & {
  sortSelectedOptions?: boolean;
};

const DropdownIndicator = <IsMulti extends boolean = true>(
  props: DropdownIndicatorProps<Option, IsMulti>
) => (
  <components.DropdownIndicator {...props}>
    <IconMagnifyingGlass size={16} />
  </components.DropdownIndicator>
);

const isOption = (item: Option | GroupBase<Option>): item is Option =>
  'value' in item;

const getSelectedValues = (
  value: PropsValue<Option> | undefined
): Set<string> => {
  if (!value) return new Set();
  if ('value' in value) return new Set([value.value]);
  return new Set(value.map((option) => option.value));
};

export const SelectionListSelect = <IsMulti extends boolean = true>({
  sortSelectedOptions = true,
  ...selectProps
}: SelectionListSelectProps<IsMulti>) => {
  // Memoized so the `Option` component keeps one identity across renders.
  // Declaring it inline made every option a fresh component type on each
  // render, remounting the whole list on any selection change — which threw
  // away each checkbox's state and, with it, the tick animation.
  const selectComponents = useMemo(
    () => ({
      DropdownIndicator,
      Option: (optionProps: OptionProps<Option, IsMulti>) => (
        <CustomOption {...optionProps} />
      ),
    }),
    []
  );
  const openingSelectedValues = useRef(getSelectedValues(selectProps.value));
  const sortedOptions = useMemo(() => {
    if (!selectProps.options) return undefined;
    if (!sortSelectedOptions) return selectProps.options;

    const flatOptions = selectProps.options.filter(isOption);
    if (flatOptions.length !== selectProps.options.length) {
      return selectProps.options;
    }

    return sortSelectedOptionsFirst(flatOptions, openingSelectedValues.current);
  }, [selectProps.options, sortSelectedOptions]);

  const handleChange = (
    allOptions: OnChangeValue<Option, IsMulti>,
    actionMeta: ActionMeta<Option>
  ) => {
    if (!selectProps.onChange) return;

    if (!Array.isArray(allOptions)) {
      selectProps.onChange(allOptions, actionMeta);
      return;
    }

    // Get limit from props as Select strips it out
    const getLimit = (opt: Option) => {
      const found = (selectProps.options as Option[]).find(
        (o) => o.value === opt.value
      );
      return found?.limit ?? Infinity;
    };

    const lowestLimit = Math.min(...allOptions.map(getLimit));

    // If we're under or at the limit, proceed normally
    if (allOptions.length <= lowestLimit) {
      selectProps.onChange(allOptions, actionMeta);
      return;
    }

    // If we're over the limit, keep only the most recent selections up to the limit
    const limitedSelection = allOptions.slice(-lowestLimit);
    selectProps.onChange(
      limitedSelection as unknown as OnChangeValue<Option, IsMulti>,
      actionMeta
    );
  };

  return (
    <Select<Option, IsMulti>
      menuIsOpen
      placeholder="Search"
      controlShouldRenderValue={false}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isClearable={false}
      backspaceRemovesValue={false}
      components={selectComponents}
      styles={{
        container: (base) => ({ ...base, fontWeight: 400, fontSize: '14px' }),
        // Search box, rows and hover below are matched to the PopoutTree
        // filter (PopoutTree.module.css `.searchField > div` + Node.module.css
        // `.nodeContainer`/`.label`) so flat and tree pickers read as one
        // control. Keep them in sync when either moves.
        control: (base) => ({
          ...base,
          height: 'var(--selection-list-control-height, 38px)',
          minHeight: 'var(--selection-list-control-height, 38px)',
          backgroundColor: 'var(--frs-menu-bg, #ffffff)',
          borderColor: 'var(--frs-border, #E2E8F0)',
          borderRadius: 'var(--ui-radius-xs, 2px)',
          boxShadow: 'var(--ui-shadow-panel, none)',
          ':hover': { borderColor: 'var(--frs-border, #CBD5E0)' },
          ':focus-within': {
            borderColor: 'var(--ui-input-focus-border, #3dc679)',
            boxShadow: '0 0 0 1px var(--ui-input-focus-border, #3dc679)',
          },
        }),
        placeholder: (base) => ({
          ...base,
          color: 'var(--ui-text-secondary, hsl(0, 0%, 50%))',
        }),
        valueContainer: (base) => ({
          ...base,
          height: '100%',
          padding: '2px 12px',
        }),
        input: (base) => ({
          ...base,
          marginTop: 0,
          marginBottom: 0,
          cursor: 'text',
          color: 'var(--frs-option-text, inherit)',
        }),
        indicatorsContainer: (base) => ({ ...base, height: '100%' }),
        indicatorSeparator: (base) => ({ ...base, display: 'none' }),
        dropdownIndicator: (base) => ({
          ...base,
          color: 'var(--frs-text-muted, hsl(0, 0%, 80%))',
          ':hover': { color: 'var(--frs-text-muted, hsl(0, 0%, 80%))' },
        }),
        menu: (base) => ({
          ...base,
          position: 'relative',
          margin: 'var(--selection-list-menu-margin-top, 8px) 0 0',
          zIndex: 'unset',
          boxShadow: 'none',
          backgroundColor: 'var(--frs-menu-bg, #ffffff)',
        }),
        menuList: (base) => ({
          ...base,
          padding: 0,
          fontSize: 'var(--ui-font-size-sm, 14px)',
          scrollbarColor: 'var(--frs-scrollbar, #e3e3e3 #fff)',
          color: 'var(--frs-option-text, #2D426A)',
        }),
        option: (base, { isFocused, isDisabled }) => ({
          ...base,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          minHeight: '32px',
          padding: '0 6px',
          color: isDisabled
            ? 'var(--frs-text-disabled, #A0AEC0)'
            : 'var(--frs-option-text, #2D426A)',
          // The tree's own ink wash, not --frs-option-hover-bg: that var also
          // skins the keyword select, which keeps its own hover.
          backgroundColor: isFocused
            ? 'rgb(var(--ui-ink-rgb, 45 66 106) / 0.05)'
            : 'transparent',
          borderColor: isFocused
            ? 'var(--frs-border, #CBD5E0)'
            : 'var(--frs-border, #E2E8F0)',
          ':active': {
            backgroundColor: 'rgb(var(--ui-ink-rgb, 45 66 106) / 0.05)',
          },
        }),
        groupHeading: (base) => ({
          ...base,
          marginBottom: '1px',
          fontSize: 'var(--chakra-fontSizes-xs)',
          color: 'var(--frs-text-muted, #96a0b5)',
          fontWeight: '600',
          letterSpacing: '0.8px',
          textTransform: 'none',
          padding: '0px',
        }),
      }}
      {...selectProps}
      options={sortedOptions}
      onChange={handleChange}
    />
  );
};
